import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import type { AdminConversation, AdminMessage, AdminQualityCheck, ConversationFeedback, ProfileRefineryMode } from '../../api/types.js';
import { formatCny } from '../../lib/kimi-k26-cost.js';
import { displayName, formatRelative, modeText, readableWechatId } from '../../lib/utils.js';
import { ConversationFeedbackPanel } from './ConversationFeedbackPanel.js';
import { ProfileMemoryPanel } from './ProfileMemoryPanel.js';
import { Avatar } from '../ui/Avatar.js';

interface ProfilePanelProps {
  conversation: AdminConversation | null;
  messages: AdminMessage[];
  quality: AdminQualityCheck | null;
  conversationFeedback: ConversationFeedback | null;
  onSaveConversationFeedback: (conversationId: number, summary: string) => Promise<void>;
  onRunProfileRefinery: (friendId: number, mode: ProfileRefineryMode) => Promise<void>;
  onResolveProfileMemoryWarnings: (friendId: number) => Promise<void>;
  conversationAiCost?: { totalCny: number; turns: number };
  /** 移动端全屏覆盖时的返回按钮；桌面端不渲染 */
  onClose?: () => void;
}

export function ProfilePanel({
  conversation,
  messages,
  quality,
  conversationFeedback,
  onSaveConversationFeedback,
  onRunProfileRefinery,
  onResolveProfileMemoryWarnings,
  conversationAiCost,
  onClose,
}: ProfilePanelProps) {
  const [tab, setTab] = useState('feedback');

  if (!conversation) {
    return (
      <>
        <header className="profile-header">
          <h2>客户画像</h2>
          {onClose ? (
            <button className="button ghost profile-back" type="button" onClick={onClose}>
              ‹ 返回
            </button>
          ) : null}
        </header>
        <div className="profile-body">
          <div className="empty-state small">
            <Avatar name="客户" size="lg" />
            <strong>未选择客户</strong>
          </div>
        </div>
      </>
    );
  }

  const name = displayName(conversation);
  const inbound = messages.filter((m) => m.direction === 'in').length;
  const outbound = messages.filter((m) => m.direction === 'out').length;

  return (
    <>
      <header className="profile-header">
        <h2>客户画像</h2>
        {onClose ? (
          <button className="button ghost profile-back" type="button" onClick={onClose}>
            ‹ 返回
          </button>
        ) : null}
      </header>
      <div className="profile-body">
        <Tabs.Root value={tab} onValueChange={setTab}>
          <Tabs.List className="profile-tabs">
            <Tabs.Trigger className={`profile-tab ${tab === 'feedback' ? 'active' : ''}`} value="feedback">
              会话评语
            </Tabs.Trigger>
            <Tabs.Trigger className={`profile-tab ${tab === 'profile' ? 'active' : ''}`} value="profile">
              客户画像
            </Tabs.Trigger>
            <Tabs.Trigger className={`profile-tab ${tab === 'memory' ? 'active' : ''}`} value="memory">
              档案卡
            </Tabs.Trigger>
            <Tabs.Trigger className={`profile-tab ${tab === 'quality' ? 'active' : ''}`} value="quality">
              质检
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="feedback">
            <ConversationFeedbackPanel
              conversation={conversation}
              feedback={conversationFeedback}
              onSave={onSaveConversationFeedback}
            />
          </Tabs.Content>

          <Tabs.Content value="profile">
            <div className="profile-card">
              <section className="profile-hero">
                <Avatar name={name} size="xl" />
                <strong>{name}</strong>
                <div className="profile-muted">
                  自定义微信号：{readableWechatId(conversation.customerWechatAlias) || '未设置'}
                </div>
                <div className="tag-row">
                  {conversation.isWhitelisted ? (
                    <span className="tag whitelist">白名单客户</span>
                  ) : (
                    <span className="tag">非白名单</span>
                  )}
                  <span className={`tag ${conversation.mode === 'manual' ? 'manual' : ''}`}>
                    {modeText(conversation.mode)}
                  </span>
                </div>
              </section>

              <section className="profile-section">
                <h3>关键信息</h3>
                <div className="profile-muted">备注：{conversation.customerRemark || '未填写'}</div>
                <div className="profile-muted">昵称：{conversation.customerNickname || '未填写'}</div>
                <div className="profile-muted">
                  最近活跃：{formatRelative(conversation.lastMessageAt)}
                </div>
              </section>

              <section className="profile-section">
                <h3>会话统计</h3>
                <div className="metric-grid">
                  <div className="metric">
                    <strong>{messages.length}</strong>
                    <span>消息数</span>
                  </div>
                  <div className="metric">
                    <strong>{conversation.unreadCount}</strong>
                    <span>未读</span>
                  </div>
                  <div className="metric">
                    <strong>{inbound}</strong>
                    <span>客户消息</span>
                  </div>
                  <div className="metric">
                    <strong>{outbound}</strong>
                    <span>回复消息</span>
                  </div>
                  <div className="metric">
                    <strong>
                      {conversationAiCost && conversationAiCost.turns > 0
                        ? formatCny(conversationAiCost.totalCny)
                        : '—'}
                    </strong>
                    <span>AI 合计</span>
                  </div>
                  <div className="metric">
                    <strong>{conversationAiCost?.turns ?? 0}</strong>
                    <span>计价回合</span>
                  </div>
                </div>
              </section>
            </div>
          </Tabs.Content>

          <Tabs.Content value="memory">
            <ProfileMemoryPanel
              conversation={conversation}
              onRunProfileRefinery={onRunProfileRefinery}
              onResolveWarnings={onResolveProfileMemoryWarnings}
            />
          </Tabs.Content>

          <Tabs.Content value="quality">
            <div className="profile-card">
              <section className="profile-section">
                <h3>质检记录</h3>
                {quality ? (
                  <div className="quality-card">
                    <strong>{quality.score} 分</strong>
                    <div className="profile-muted">{quality.summary}</div>
                    {quality.issues && quality.issues.length > 0 ? (
                      <div className="quality-issues">
                        {quality.issues.map((issue, i) => (
                          <div key={i}>
                            {issue.dimension}: {issue.detail}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="profile-muted">暂无质检记录</div>
                )}
              </section>
            </div>
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </>
  );
}
